import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { FormInstance } from "antd";
import dayjs from "dayjs";
import { releaseYearValue } from "../../utils/year";
import { episodeDurationParts } from "../../utils/duration";
import { mediaUrl } from "../../../../utils/functions/media/mediaUrl";
import _, { capitalize } from "lodash";
import toast from "react-hot-toast";
import {
  MdEdit,
  MdError,
  MdImage,
  MdInfoOutline,
  MdLiveTv,
  MdMovieCreation,
  MdPlayCircleOutline,
  MdShield,
} from "react-icons/md";
import { useAtomValue } from "jotai";
import { masterAtom } from "../../../../atoms/masters.atom";
import { valueToLabel } from "../../../../utils/functions/misc/valueToLabel";
import { wait } from "../../../../utils/functions/misc/wait";
import { Poster } from "../../../../types/Poster";
import { SetState } from "../../../../types/SetState";
import { Season } from "../../../../models/season.model";
import { SeriesProfile } from "../../../../models/series-profile.model";
import { StepCompletionStatus } from "../../../films/components/step-nav.component";
import { config } from "../../../../config/config";
import { colors } from "../../../../theme/colors";
import {
  Section,
  card,
} from "../../../films/components/section-card.component";
import {
  EpisodeError,
  SeasonError,
  ValidationResult,
  getSeasonErrors,
  validateSeriesDraft,
} from "../../utils/validation";

export const ReviewStep = ({
  form,
  seasons,
  verticalPoster,
  horizontalPoster,
  completedSteps,
  setCurrentStep,
  synopsis,
  visible = true,
  setPosterErrors,
  activeSeasonIndex,
  setActiveSeasonIndex,
  setStepFourTab,
  setStepFourShowErrors,
  setActiveEpisodeIndex: setParentActiveEpisodeIndex,
  onValidate,
}: {
  synopsis: string;
  visible?: boolean;
  form: FormInstance<SeriesProfile>;
  completedSteps: StepCompletionStatus[];
  seasons: Season[];
  verticalPoster: Poster[];
  horizontalPoster: Poster[];
  currentStep: number;
  setCurrentStep: SetState<number>;
  setPosterErrors: SetState<[boolean, boolean]>;
  setSeasonPosterErrors: SetState<[boolean, boolean][]>;
  setEpisodePosterErrors: SetState<[boolean, boolean][]>;
  activeSeasonIndex: number;
  setActiveSeasonIndex: SetState<number>;
  stepFourTab: 0 | 1;
  setStepFourTab: SetState<0 | 1>;
  setStepFourShowErrors: SetState<boolean>;
  setActiveEpisodeIndex?: SetState<number>;
  onValidate?: (result: ValidationResult) => void;
}) => {
  const master = useAtomValue(masterAtom);
  const [activeEpisodeIndex, setActiveEpisodeIndex] = useState(0);
  const [film, setFilm] = useState<Partial<SeriesProfile>>({});

  useEffect(() => {
    const next = { ...form.getFieldsValue(true) };
    if (Object.keys(next).length > 0) setFilm(next);
  }, [form]);

  const seasonErrors = useMemo(() => {
    const errors: SeasonError[] = [];
    seasons.forEach((season, seasonIndex) => {
      const seasonErrs = getSeasonErrors(season);
      if (seasonErrs.length > 0) {
        errors.push({
          seasonIndex,
          seasonTitle: season.season_title || `Season ${seasonIndex + 1}`,
          errors: seasonErrs,
        });
      }
    });
    return errors;
  }, [seasons]);

  const hasAnySeasonErrors = seasonErrors.length > 0;

  const episodeErrors = useMemo(
    () =>
      validateSeriesDraft({
        film,
        synopsis,
        verticalPoster,
        horizontalPoster,
        seasons,
      }).episodeErrors,
    [film, synopsis, verticalPoster, horizontalPoster, seasons]
  );

  const seasonsWithEpisodeErrors = useMemo(() => {
    const set = new Set<number>();
    episodeErrors.forEach((err) => set.add(err.seasonIndex));
    return Array.from(set);
  }, [episodeErrors]);

  const hasAnyEpisodeErrors = episodeErrors.length > 0;

  useEffect(() => {
    if (!onValidate) return;
    onValidate(
      validateSeriesDraft({
        film,
        synopsis,
        verticalPoster,
        horizontalPoster,
        seasons,
      })
    );
  }, [film, synopsis, verticalPoster, horizontalPoster, seasons, onValidate]);

  const onGoToStep = useCallback(
    async (step: number) => {
      setCurrentStep(step);
      await wait(300);
    },
    [setCurrentStep]
  );

  const onGoToSeasons = useCallback(async () => {
    setCurrentStep(2);
    await wait(200);
    const firstInvalid =
      seasonErrors.length > 0 ? seasonErrors[0].seasonIndex : 0;
    setActiveSeasonIndex(firstInvalid);
    setStepFourTab(0);
    setStepFourShowErrors(true);
  }, [setCurrentStep, seasonErrors, setActiveSeasonIndex, setStepFourTab, setStepFourShowErrors]);

  const goToEpisodes = useCallback(async () => {
    await onGoToStep(3);
    if (episodeErrors.length > 0) {
      const first = episodeErrors[0];
      setActiveSeasonIndex(first.seasonIndex);
      setActiveEpisodeIndex(first.episodeIndex);
      setParentActiveEpisodeIndex?.(first.episodeIndex);
    }
  }, [
    onGoToStep,
    episodeErrors,
    setActiveSeasonIndex,
    setParentActiveEpisodeIndex,
  ]);

  const goToPosters = useCallback(async () => {
    await onGoToStep(1);
    const errs: [boolean, boolean] = [false, false];
    if (!verticalPoster.filter((p) => p.type === 0).length) {
      errs[0] = true;
      toast.error("Please upload at least one vertical poster!");
    }
    if (!horizontalPoster.filter((p) => p.type === 0).length) {
      errs[1] = true;
      toast.error("Please upload at least one horizontal poster!");
    }
    setPosterErrors(errs);
  }, [onGoToStep, verticalPoster, horizontalPoster, setPosterErrors]);

  const heroPoster = mediaUrl(
    verticalPoster.find((p) => p.type === 0 && p.url)?.url ??
      verticalPoster.find((p) => p.url)?.url ??
      horizontalPoster.find((p) => p.url)?.url
  );

  const activeSeason = seasons[activeSeasonIndex] ?? ({} as Season);
  const activeEpisode = activeSeason?.episodes?.[activeEpisodeIndex];

  const episodeDuration = (() => {
    const parts = episodeDurationParts(activeEpisode?.duration);
    if (!parts.mm && !parts.ss) return "";
    return `${parts.mm.padStart(2, "0")}:${parts.ss.padStart(2, "0")}`;
  })();

  return (
    <Wrapper style={{ display: visible ? "flex" : "none" }}>
      <Hero>
        {heroPoster ? (
          <HeroPoster src={heroPoster} />
        ) : (
          <HeroPosterPlaceholder>No poster</HeroPosterPlaceholder>
        )}
        <HeroBody>
          <HeroEyebrow>Final review</HeroEyebrow>
          <HeroTitle>{film.title || "Untitled series"}</HeroTitle>
          {film.english_title && film.english_title !== film.title ? (
            <HeroTagline>{film.english_title}</HeroTagline>
          ) : null}
          <HeroMeta>
            {film.custom_name ? (
              <HeroMetaItem>
                {config.public_site_host}/{film.custom_name}
              </HeroMetaItem>
            ) : null}
            {film.language ? (
              <HeroChip>{capitalize(film.language)}</HeroChip>
            ) : null}
            {film.genre?.length ? (
              <HeroChip>
                {film.genre.map(_.capitalize).slice(0, 3).join(" · ")}
              </HeroChip>
            ) : null}
            <HeroChip>
              {seasons.length} season{seasons.length === 1 ? "" : "s"}
            </HeroChip>
            <HeroChip>
              {seasons.reduce(
                (sum, season) => sum + (season.episodes?.length ?? 0),
                0
              )}{" "}
              episodes
            </HeroChip>
          </HeroMeta>
        </HeroBody>
      </Hero>

      <Section
        icon={<MdInfoOutline />}
        title="Series Details"
        subtitle="Title, language, genre and synopsis."
        action={
          <EditButton
            $error={completedSteps[0] === false}
            onClick={() => onGoToStep(0)}
          >
            <MdEdit /> Edit
          </EditButton>
        }
      >
        <KvGrid>
          <Kv label="Original Title" value={film.title} required />
          <Kv label="English Title" value={film.english_title} />
          <Kv label="Custom URL" value={film.custom_name} required />
          <Kv
            label="Genre"
            value={film.genre?.map(_.capitalize)?.join(", ")}
            required
          />
          <Kv
            label="Original Language"
            value={_.capitalize(film.language ?? "")}
            required
          />
          <Kv
            label="Synopsis"
            value={synopsis}
            required
            fullWidth
            multiline
          />
        </KvGrid>
      </Section>

      <Section
        icon={<MdShield />}
        title="Censor Ratings"
        subtitle="Content advisories across six categories."
        action={
          <EditButton
            $error={completedSteps[0] === false}
            onClick={() => onGoToStep(0)}
          >
            <MdEdit /> Edit
          </EditButton>
        }
      >
        <RatingGrid>
          <RatingPill
            label="Language"
            value={valueToLabel(
              "language_ratings",
              film.language_rating,
              master
            )}
          />
          <RatingPill
            label="Violence"
            value={valueToLabel(
              "violence_ratings",
              film.violence_rating,
              master
            )}
          />
          <RatingPill
            label="Hateful & Derogatory"
            value={valueToLabel(
              "hateful_ratings",
              film.hateful_rating,
              master
            )}
          />
          <RatingPill
            label="Adult Content"
            value={valueToLabel(
              "adult_content_ratings",
              film.adult_content_rating,
              master
            )}
          />
          <RatingPill
            label="Drugs"
            value={valueToLabel("drugs_ratings", film.drug_rating, master)}
          />
          <RatingPill
            label="Harmful / Dangerous Acts"
            value={valueToLabel(
              "harmful_ratings",
              film.harmful_rating,
              master
            )}
          />
        </RatingGrid>
      </Section>

      <Section
        icon={<MdImage />}
        title="Series Posters"
        subtitle="Artwork representing the whole series."
        action={
          <EditButton
            $error={completedSteps[1] === false}
            $warning={completedSteps[1] === "warning"}
            onClick={goToPosters}
          >
            <MdEdit /> Edit
          </EditButton>
        }
      >
        <PosterGroup
          label="Vertical posters"
          posters={verticalPoster}
          error={!verticalPoster.filter((p) => p.type === 0).length}
          emptyMessage="No vertical posters uploaded"
        />
        <PosterGroup
          label="Horizontal posters"
          posters={horizontalPoster}
          error={!horizontalPoster.filter((p) => p.type === 0).length}
          emptyMessage="No horizontal posters uploaded"
        />
      </Section>

      <Section
        icon={<MdLiveTv />}
        title={`Seasons (${seasons.length})`}
        subtitle="Browse season-level details and posters."
        action={
          <EditButton
            $error={hasAnySeasonErrors || completedSteps[2] === false}
            onClick={onGoToSeasons}
          >
            <MdEdit /> Edit
          </EditButton>
        }
      >
        {hasAnySeasonErrors ? (
          <ErrorMatrix>
            <ErrorMatrixHead>
              <MdError size={18} color={colors.error.hex} />
              <span>
                {seasonErrors.length} season
                {seasonErrors.length > 1 ? "s" : ""} need attention
              </span>
            </ErrorMatrixHead>
            <ErrorMatrixList>
              {seasonErrors.map((err, idx) => (
                <ErrorMatrixItem
                  key={idx}
                  onClick={async () => {
                    setActiveSeasonIndex(err.seasonIndex);
                    setStepFourTab(0);
                    setStepFourShowErrors(true);
                    await onGoToStep(2);
                  }}
                >
                  <ErrorItemLabel>{err.seasonTitle}</ErrorItemLabel>
                  <ErrorItemDetail>{err.errors.join(" · ")}</ErrorItemDetail>
                </ErrorMatrixItem>
              ))}
            </ErrorMatrixList>
          </ErrorMatrix>
        ) : null}

        {seasons.length === 0 ? (
          <EmptyText>No seasons added.</EmptyText>
        ) : (
          <>
            <ChipScroller>
              {seasons.map((season, index) => {
                const invalid = seasonErrors.some(
                  (e) => e.seasonIndex === index
                );
                return (
                  <Chip
                    key={season.id ?? `season-${index}`}
                    $active={activeSeasonIndex === index}
                    $invalid={invalid}
                    type="button"
                    onClick={() => setActiveSeasonIndex(index)}
                  >
                    <ChipNumber>{index + 1}</ChipNumber>
                    <ChipLabel>
                      {season.season_title || `Season ${index + 1}`}
                    </ChipLabel>
                  </Chip>
                );
              })}
            </ChipScroller>

            <KvGrid>
              <Kv
                label="Season Title"
                value={activeSeason.season_title}
                required
              />
              <Kv
                label="Release Status"
                value={capitalize(activeSeason.release_status ?? "")}
                required
              />
              {activeSeason.release_status === "released" ? (
                <Kv
                  label="Year of First Release"
                  value={
                    releaseYearValue(activeSeason.year_of_release)?.format(
                      "YYYY"
                    ) ?? ""
                  }
                  required
                />
              ) : null}
              {activeSeason.release_status === "released" ? (
                <Kv
                  label="Medium of Release"
                  value={capitalize(activeSeason.medium_of_release ?? "")}
                  required
                />
              ) : null}
              {activeSeason.release_status === "released" ? (
                <Kv
                  label="Release Platform"
                  value={activeSeason.release_platform}
                  required
                />
              ) : null}
              <Kv
                label="Synopsis"
                value={activeSeason.season_synopsis}
                required
                fullWidth
                multiline
              />
            </KvGrid>

            <PosterGroup
              label="Vertical posters"
              posters={activeSeason.vertical_poster ?? []}
              error={
                !(activeSeason.vertical_poster ?? []).filter(
                  (p) => p.type === 0
                ).length
              }
              emptyMessage="No vertical posters uploaded"
            />
            <PosterGroup
              label="Horizontal posters"
              posters={activeSeason.horizontal_poster ?? []}
              error={
                !(activeSeason.horizontal_poster ?? []).filter(
                  (p) => p.type === 0
                ).length
              }
              emptyMessage="No horizontal posters uploaded"
            />
          </>
        )}
      </Section>

      <Section
        icon={<MdMovieCreation />}
        title={`Episodes (${seasons.reduce(
          (sum, s) => sum + (s.episodes?.length ?? 0),
          0
        )})`}
        subtitle="Pick a season, then browse its episodes."
        action={
          <EditButton
            $error={hasAnyEpisodeErrors || completedSteps[3] === false}
            onClick={goToEpisodes}
          >
            <MdEdit /> Edit
          </EditButton>
        }
      >
        {hasAnyEpisodeErrors ? (
          <ErrorMatrix>
            <ErrorMatrixHead>
              <MdError size={18} color={colors.error.hex} />
              <span>
                {episodeErrors.length} episode
                {episodeErrors.length > 1 ? "s" : ""} need attention
              </span>
            </ErrorMatrixHead>
            <ErrorMatrixList>
              {episodeErrors.map((err: EpisodeError, idx) => (
                <ErrorMatrixItem
                  key={idx}
                  onClick={async () => {
                    setActiveSeasonIndex(err.seasonIndex);
                    setActiveEpisodeIndex(err.episodeIndex);
                    setParentActiveEpisodeIndex?.(err.episodeIndex);
                    await onGoToStep(3);
                  }}
                >
                  <ErrorItemLabel>
                    {err.seasonTitle} → {err.episodeTitle}
                  </ErrorItemLabel>
                  <ErrorItemDetail>{err.errors.join(" · ")}</ErrorItemDetail>
                </ErrorMatrixItem>
              ))}
            </ErrorMatrixList>
          </ErrorMatrix>
        ) : null}

        {seasons.length === 0 ||
        seasons[activeSeasonIndex]?.episodes?.length === 0 ? (
          <EmptyText>No episodes added.</EmptyText>
        ) : (
          <>
            <PickerRow>
              <PickerLabel>Season</PickerLabel>
              <PickerSelect
                value={activeSeasonIndex}
                onChange={(e) => {
                  setActiveSeasonIndex(+e.target.value);
                  setActiveEpisodeIndex(0);
                }}
              >
                {seasons.map((season, i) => (
                  <option key={season.id ?? i} value={i}>
                    {season.season_title || `Season ${i + 1}`}
                    {seasonsWithEpisodeErrors.includes(i) ? " ⚠" : ""}
                  </option>
                ))}
              </PickerSelect>
            </PickerRow>

            <ChipScroller>
              {(seasons[activeSeasonIndex]?.episodes ?? []).map(
                (episode, index) => {
                  const invalid = episodeErrors.some(
                    (err) =>
                      err.seasonIndex === activeSeasonIndex &&
                      err.episodeIndex === index
                  );
                  return (
                    <Chip
                      key={episode.id ?? `episode-${index}`}
                      $active={activeEpisodeIndex === index}
                      $invalid={invalid}
                      type="button"
                      onClick={() => setActiveEpisodeIndex(index)}
                    >
                      <ChipNumber>{index + 1}</ChipNumber>
                      <ChipLabel>
                        {episode.title || `Episode ${index + 1}`}
                      </ChipLabel>
                    </Chip>
                  );
                }
              )}
            </ChipScroller>

            <KvGrid>
              <Kv label="Episode Name" value={activeEpisode?.title} />
              <Kv label="Duration (mm:ss)" value={episodeDuration} />
              <Kv label="Tagline" value={activeEpisode?.tagline} />
              <Kv
                label="Date of Completion"
                value={
                  activeEpisode?.release_date
                    ? dayjs(activeEpisode.release_date as any).isValid()
                      ? dayjs(activeEpisode.release_date as any).format(
                          "YYYY-MM-DD"
                        )
                      : ""
                    : ""
                }
              />
              <Kv
                label="Shooting Format"
                value={valueToLabel(
                  "shooting_format",
                  activeEpisode?.shooting_format,
                  master
                )}
                required
              />
              <Kv
                label="Original Aspect Ratio"
                value={valueToLabel(
                  "aspect_ratio",
                  activeEpisode?.aspect_ratio,
                  master
                )}
                required
              />
              <Kv
                label="Synopsis"
                value={activeEpisode?.synopsis}
                required
                fullWidth
                multiline
              />
            </KvGrid>

            <PosterGroup
              label="Vertical posters"
              posters={activeEpisode?.vertical_poster ?? []}
              error={
                !(activeEpisode?.vertical_poster ?? []).filter(
                  (p) => p.type === 0
                ).length
              }
              emptyMessage="No vertical posters uploaded"
            />
            <PosterGroup
              label="Horizontal posters"
              posters={activeEpisode?.horizontal_poster ?? []}
              error={
                !(activeEpisode?.horizontal_poster ?? []).filter(
                  (p) => p.type === 0
                ).length
              }
              emptyMessage="No horizontal posters uploaded"
            />
          </>
        )}
      </Section>

      <ReadyBanner $ready={!hasAnySeasonErrors && !hasAnyEpisodeErrors}>
        <ReadyIcon>
          <MdPlayCircleOutline />
        </ReadyIcon>
        <ReadyText>
          <ReadyTitle>
            {!hasAnySeasonErrors && !hasAnyEpisodeErrors
              ? "Ready to submit"
              : "Some items still need attention"}
          </ReadyTitle>
          <ReadyHint>
            {!hasAnySeasonErrors && !hasAnyEpisodeErrors
              ? "Use the button at the bottom of the page to save or create your series."
              : "Resolve the highlighted issues above before submitting."}
          </ReadyHint>
        </ReadyText>
      </ReadyBanner>
    </Wrapper>
  );
};

const Kv = ({
  label,
  value,
  required,
  fullWidth,
  multiline,
}: {
  label: string;
  value?: ReactNode;
  required?: boolean;
  fullWidth?: boolean;
  multiline?: boolean;
}) => {
  const missing = required && !value;
  return (
    <KvShell $fullWidth={fullWidth}>
      <KvLabel $error={!!missing}>
        {label}
        {required ? <RequiredMark>*</RequiredMark> : null}
      </KvLabel>
      <KvValue $missing={!value} $multiline={multiline}>
        {value || (missing ? "Required" : "—")}
      </KvValue>
    </KvShell>
  );
};

const RatingPill = ({
  label,
  value,
}: {
  label: string;
  value?: ReactNode;
}) => (
  <RatingShell $missing={!value}>
    <RatingLabel>{label}</RatingLabel>
    <RatingValue $missing={!value}>{value || "Required"}</RatingValue>
  </RatingShell>
);

const PosterGroup = ({
  label,
  posters,
  emptyMessage,
  error,
}: {
  label: string;
  posters: Poster[];
  emptyMessage: string;
  error?: boolean;
}) => {
  const valid = posters.filter((p) => !!p.url);
  return (
    <PosterGroupShell>
      <PosterGroupLabel $error={!!error}>{label}</PosterGroupLabel>
      {valid.length === 0 ? (
        <EmptyText $error={!!error}>{emptyMessage}</EmptyText>
      ) : (
        <PosterScroller>
          {valid.map((p, i) => (
            <PosterThumb
              key={`${p.url}-${i}`}
              src={mediaUrl(p.url)}
              onClick={() => p.url && window.open(mediaUrl(p.url))}
            />
          ))}
        </PosterScroller>
      )}
    </PosterGroupShell>
  );
};

const Wrapper = styled.div`
  width: 100%;
  flex-direction: column;
  gap: 1.6rem;
`;

const Hero = styled.div`
  display: flex;
  align-items: stretch;
  gap: 1.8rem;
  width: 100%;
  background: ${card.bg};
  border: 1px solid ${card.border};
  border-radius: 1.2rem;
  box-shadow: ${card.shadow};
  padding: 1.8rem;
  overflow: hidden;

  @media (max-width: 48em) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeroPoster = styled.img`
  flex: 0 0 auto;
  width: 12rem;
  height: 16rem;
  border-radius: 0.8rem;
  object-fit: cover;
  border: 1px solid ${card.border};
  background: ${card.innerBg};
`;

const HeroPosterPlaceholder = styled.div`
  flex: 0 0 auto;
  width: 12rem;
  height: 16rem;
  border-radius: 0.8rem;
  border: 1px dashed ${card.border};
  background: ${card.innerBg};
  color: ${colors.text.hex};
  opacity: 0.45;
  font-size: 1.15rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const HeroBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.6rem;
`;

const HeroEyebrow = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${colors.primary.hex};
  opacity: 0.85;
`;

const HeroTitle = styled.h1`
  margin: 0;
  font-size: 2.6rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${colors.text.hex};
  line-height: 1.1;
`;

const HeroTagline = styled.div`
  font-size: 1.4rem;
  font-style: italic;
  color: ${colors.text.hex};
  opacity: 0.75;
  line-height: 1.4;
`;

const HeroMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.4rem;
`;

const HeroMetaItem = styled.div`
  font-size: 1.2rem;
  color: ${colors.text.hex};
  opacity: 0.6;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, "Courier New",
    monospace;
`;

const HeroChip = styled.span`
  display: inline-flex;
  padding: 0.15rem 0.7rem;
  font-size: 1.1rem;
  font-weight: 500;
  color: ${colors.primary.hex};
  background: ${card.innerBg};
  border: 1px solid ${card.border};
  border-radius: 999px;
`;

const EditButton = styled.button<{ $error?: boolean; $warning?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 3.2rem;
  padding: 0 1.2rem;
  background: ${({ $error, $warning }) =>
    $error
      ? "rgba(239, 35, 60, 0.10)"
      : $warning
        ? "rgba(217, 119, 6, 0.12)"
        : card.innerBg};
  border: 1px solid
    ${({ $error, $warning }) =>
      $error ? colors.error.hex : $warning ? colors.warning.hex : card.border};
  border-radius: 0.6rem;
  color: ${({ $error, $warning }) =>
    $error ? colors.error.hex : $warning ? colors.warning.hex : colors.primary.hex};
  font-size: 1.2rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  & svg {
    font-size: 1.4rem;
  }

  &:hover {
    background: ${({ $error, $warning }) =>
      $error
        ? "rgba(239, 35, 60, 0.18)"
        : $warning
          ? "rgba(217, 119, 6, 0.2)"
          : card.accentSoft};
    border-color: ${({ $error, $warning }) =>
      $error ? colors.error.hex : $warning ? colors.warning.hex : colors.primary.hex};
  }
`;

const KvGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.2rem 2rem;
  width: 100%;
  container-type: inline-size;

  @container (max-width: 32rem) {
    grid-template-columns: 1fr;
  }
`;

const KvShell = styled.div<{ $fullWidth?: boolean }>`
  ${({ $fullWidth }) => ($fullWidth ? "grid-column: 1 / -1;" : "")}
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  min-width: 0;
`;

const KvLabel = styled.div<{ $error?: boolean }>`
  font-size: 1.15rem;
  font-weight: 500;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${({ $error }) => ($error ? colors.error.hex : colors.text.hex)};
  opacity: ${({ $error }) => ($error ? 0.95 : 0.55)};
`;

const RequiredMark = styled.span`
  color: ${colors.error.hex};
  margin-left: 0.2rem;
`;

const KvValue = styled.div<{ $missing?: boolean; $multiline?: boolean }>`
  font-size: 1.35rem;
  font-weight: 500;
  color: ${colors.text.hex};
  opacity: ${({ $missing }) => ($missing ? 0.45 : 0.95)};
  line-height: 1.45;
  white-space: ${({ $multiline }) => ($multiline ? "pre-wrap" : "normal")};
  word-break: break-word;
`;

const RatingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.9rem 1.2rem;
  width: 100%;
  container-type: inline-size;

  @container (max-width: 44rem) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @container (max-width: 24rem) {
    grid-template-columns: 1fr;
  }
`;

const RatingShell = styled.div<{ $missing?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  padding: 1rem 1.2rem;
  background: ${card.innerBg};
  border: 1px solid
    ${({ $missing }) => ($missing ? colors.error.hex : card.border)};
  border-radius: 0.7rem;
`;

const RatingLabel = styled.div`
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${colors.text.hex};
  opacity: 0.55;
`;

const RatingValue = styled.div<{ $missing?: boolean }>`
  font-size: 1.35rem;
  font-weight: 600;
  color: ${({ $missing }) =>
    $missing ? colors.error.hex : colors.primary.hex};
  opacity: ${({ $missing }) => ($missing ? 0.85 : 1)};
`;

const ChipScroller = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
`;

const Chip = styled.button<{ $active?: boolean; $invalid?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem 0.5rem 0.5rem;
  background: ${({ $active, $invalid }) =>
    $active
      ? colors.primary.hex
      : $invalid
        ? "rgba(239, 35, 60, 0.08)"
        : card.innerBg};
  color: ${({ $active, $invalid }) =>
    $active
      ? colors.white.hex
      : $invalid
        ? colors.error.hex
        : colors.text.hex};
  border: 1px solid
    ${({ $active, $invalid }) =>
      $active
        ? colors.primary.hex
        : $invalid
          ? colors.error.hex
          : card.border};
  border-radius: 999px;
  font-size: 1.2rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;

  &:hover {
    border-color: ${colors.primary.hex};
  }
`;

const ChipNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.1rem;
  height: 2.1rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  font-size: 1.15rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;

  ${Chip}[data-inactive] & {
    background: ${card.bg};
    color: ${colors.primary.hex};
  }
`;

const ChipLabel = styled.span`
  font-size: 1.2rem;
  font-weight: 500;
  max-width: 16rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PickerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  flex-wrap: wrap;
`;

const PickerLabel = styled.span`
  font-size: 1.2rem;
  font-weight: 500;
  color: ${colors.text.hex};
  opacity: 0.7;
`;

const PickerSelect = styled.select`
  height: 3.4rem;
  padding: 0 0.9rem;
  background: ${card.innerBg};
  color: ${colors.text.hex};
  border: 1px solid ${card.border};
  border-radius: 0.6rem;
  font-size: 1.25rem;
  cursor: pointer;
  min-width: 18rem;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${colors.primary.hex};
  }
`;

const PosterGroupShell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  width: 100%;
  min-width: 0;

  & + & {
    margin-top: 1rem;
  }
`;

const PosterGroupLabel = styled.div<{ $error?: boolean }>`
  font-size: 1.15rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ $error }) => ($error ? colors.error.hex : colors.text.hex)};
  opacity: ${({ $error }) => ($error ? 0.95 : 0.6)};
`;

const PosterScroller = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
`;

const PosterThumb = styled.img`
  height: 10rem;
  width: auto;
  max-width: 100%;
  border-radius: 0.6rem;
  border: 1px solid ${card.border};
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.15s, border-color 0.15s;
  background: ${card.innerBg};

  &:hover {
    transform: translateY(-1px);
    border-color: ${colors.primary.hex};
  }
`;

const EmptyText = styled.span<{ $error?: boolean }>`
  font-size: 1.2rem;
  color: ${({ $error }) => ($error ? colors.error.hex : colors.text.hex)};
  opacity: ${({ $error }) => ($error ? 0.95 : 0.55)};
`;

const ErrorMatrix = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  padding: 1.2rem 1.4rem;
  background: rgba(239, 35, 60, 0.06);
  border: 1px solid rgba(239, 35, 60, 0.32);
  border-radius: 0.9rem;
`;

const ErrorMatrixHead = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.2rem;
  font-weight: 600;
  color: ${colors.error.hex};
`;

const ErrorMatrixList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ErrorMatrixItem = styled.button`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  align-items: flex-start;
  text-align: left;
  padding: 0.7rem 1rem;
  background: ${card.innerBg};
  border: 1px solid rgba(239, 35, 60, 0.32);
  border-radius: 0.7rem;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: rgba(239, 35, 60, 0.06);
    border-color: ${colors.error.hex};
  }
`;

const ErrorItemLabel = styled.span`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${colors.text.hex};
`;

const ErrorItemDetail = styled.span`
  font-size: 1.15rem;
  color: ${colors.error.hex};
  opacity: 0.85;
`;

const ReadyBanner = styled.div<{ $ready?: boolean }>`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  padding: 1.4rem 1.6rem;
  background: ${({ $ready }) =>
    $ready ? "rgba(46, 160, 67, 0.08)" : "rgba(239, 35, 60, 0.06)"};
  border: 1px solid
    ${({ $ready }) => ($ready ? "rgba(46, 160, 67, 0.4)" : "rgba(239, 35, 60, 0.32)")};
  border-radius: 1rem;
`;

const ReadyIcon = styled.div`
  flex: 0 0 auto;
  width: 4rem;
  height: 4rem;
  border-radius: 0.8rem;
  background: ${card.innerBg};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 2.1rem;
  color: ${colors.primary.hex};
`;

const ReadyText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
`;

const ReadyTitle = styled.div`
  font-size: 1.45rem;
  font-weight: 600;
  color: ${colors.text.hex};
`;

const ReadyHint = styled.div`
  font-size: 1.2rem;
  color: ${colors.text.hex};
  opacity: 0.65;
  line-height: 1.4;
`;
